import {
  post,
  get,
  clearSession,
  currentSession,
  saveSession,
  type Session,
  type SessionUser,
} from './client'
import { ouvrirRuntimeIdentite, runtimeIdentite } from '@/identity/runtime'

/**
 * Surface d'authentification. Le module `client` sait *stocker* une session ;
 * c'est ici qu'on sait comment on l'*obtient*. Cette séparation permet de
 * changer de fournisseur d'identité (OIDC, SSO) sans toucher au client HTTP.
 */

export interface LoginPayload {
  email: string
  password: string
  /**
   * Intention signée de lancement (BBG-33), reçue de la requête d'URL
   * (`?intention=…`) quand la connexion vient d'une application beTool.
   * Optionnelle : une connexion sans lancement visé reste la norme en V1.
   * Le serveur la vérifie (signature, expiration, destination autorisée
   * exacte) — l'interface ne fait QUE la transporter, jamais l'interpréter.
   */
  intention?: string
}

/**
 * Échange une intention de lancement contre une session (BBG-125, BBG-158).
 *
 * C'est le chemin de l'écran de connexion COMMUN : le composant embarqué
 * authentifie chez la plateforme et remet une intention à usage unique, que
 * cette fonction consomme.
 *
 * `/auth/me` est relu AVEC le porteur AVANT d'enregistrer la session : le
 * contexte de lancement ne porte ni rôle ni nom d'organisation, et enregistrer
 * une session à moitié remplie ferait bâtir l'interface sur des valeurs
 * d'attente — que le reste du code prendrait pour des faits.
 */
export async function consommerLancement(jeton: string): Promise<Session> {
  const consommation = await post<{
    session?: { accessToken: string; refreshToken: string }
    identite?: { id?: string }
  }>('/lancements/consommer', { jeton })

  const jetons = consommation.session
  if (!jetons?.accessToken || !jetons.refreshToken) {
    // La plateforme a consommé l'intention sans rendre de session : le
    // lancement a réussi côté serveur mais nous n'avons pas de quoi ouvrir.
    // Le dire, plutôt que d'échouer plus loin sur un 401 inexplicable.
    throw new Error('La plateforme n’a pas remis de session pour ce lancement.')
  }

  const user = await get<SessionUser>('/auth/me', {
    headers: { Authorization: `Bearer ${jetons.accessToken}` },
  })
  // L'identité rendue avec la session (`identite.id`, la clé de raccordement) :
  // c'est elle que l'onglet incarnera (identity/adoption.ts).
  const identite = consommation.identite?.id
  const session: Session = identite ? { ...jetons, user, identiteBetool: identite } : { ...jetons, user }
  saveSession(session)

  return session
}

/**
 * Déconnexion. Le jeton de rafraîchissement est révoqué côté serveur, mais la
 * session locale est effacée QUOI QU'IL ARRIVE : si l'appel échoue (hors ligne,
 * API redémarrée), l'utilisateur doit tout de même se retrouver déconnecté sur
 * ce poste. L'inverse — rester connecté parce que le réseau est tombé — serait
 * un défaut de sécurité.
 */
export async function logout(): Promise<void> {
  const refreshToken = currentSession()?.refreshToken
  try {
    if (refreshToken) await post<void>('/auth/logout', { refreshToken })
  } catch {
    // Silence volontaire : voir ci-dessus.
  } finally {
    clearSession()
  }
}

/** Récupère l'utilisateur courant depuis le serveur (vérifie aussi le jeton). */
export function me(): Promise<SessionUser> {
  return get<SessionUser>('/auth/me')
}

/**
 * Retrouve la session à partir du slot de CET onglet (BBG-233, mode trousseau).
 *
 * ─── Pourquoi une page rechargée ne sait plus qui elle est ───────────────────
 *
 * En mode trousseau, rien de la session ne survit au rechargement dans la page :
 * `loadSession()` ne lit aucun stockage, par construction (RFC §22). Mais rien
 * n'est perdu pour autant — le slot actif de l'onglet vit dans `sessionStorage`
 * et son secret est scellé dans le cookie du trousseau. Il manquait seulement
 * quelqu'un pour aller les chercher au démarrage, faute de quoi une session
 * parfaitement vivante repartait à l'écran de connexion à chaque F5.
 *
 * ─── Ce que cette fonction fait, et dans cet ordre ───────────────────────────
 *
 * 1. Un listage AUTORITAIRE : sans lui, `currentSession()` du runtime ne rend
 *    rien — sa matière est le dernier listage, et au démarrage il n'y en a pas
 *    encore eu (le seul appelant qui liste est le widget, monté plus tard).
 * 2. `GET /auth/me`, dont l'intercepteur signe la requête avec une preuve
 *    courte achetée au BFF. C'est le serveur qui tranche : nous ne décidons
 *    jamais localement qu'une session est valide.
 * 3. L'enregistrement de la session — SANS jeton : le porteur reste scellé,
 *    c'est la forme normale de ce mode (cf. le type `Session`).
 *
 * ─── Ce qu'elle ne fait pas : deviner ────────────────────────────────────────
 *
 * Aucun slot actif → `null`, sans un seul appel réseau : c'est un navigateur
 * qui n'a jamais ouvert de session ici, pas une erreur. Un refus du serveur
 * remonte tel quel à l'appelant, qui décide d'envoyer vers l'écran de connexion.
 * Une PANNE, elle, ne doit jamais déconnecter — ne pas joindre le BFF n'apprend
 * rien sur la validité d'une session, et le distinguer est la responsabilité de
 * l'appelant, qui seul sait ce qu'il fera du verdict.
 */
export async function amorcerSessionDepuisSlot(): Promise<Session | null> {
  // Le runtime est OUVERT ici s'il ne l'est pas déjà : le seul autre appelant
  // est `AppLayout`, monté bien après cette étape et seulement sur les écrans
  // qui portent le gabarit. Dépendre de lui ferait de l'amorçage un effet de
  // bord de l'ordre de montage — vert sur un écran, muet sur un autre.
  const runtime = runtimeIdentite() ?? ouvrirRuntimeIdentite()

  // Le listage peut échouer (BFF injoignable) : on laisse remonter, l'appelant
  // distingue panne et refus. Le faire taire ici transformerait toute panne en
  // déconnexion silencieuse.
  await runtime.listBrowserSessions()
  if (runtime.currentSession() === null) return null

  const user = await get<SessionUser>('/auth/me')
  // Amorcée DEPUIS le slot de l'onglet : son identité est celle de la session.
  const identite = runtime.currentSession()?.identityId
  const session: Session = identite ? { user, identiteBetool: identite } : { user }
  saveSession(session)
  return session
}

/**
 * Demande un lien de réinitialisation de mot de passe.
 *
 * Le serveur répond 202 QUELLE QUE SOIT l'existence du compte (anti-énumération)
 * : l'interface doit donc toujours afficher le même message de confirmation,
 * jamais « compte inconnu ».
 */
export function requestPasswordReset(email: string): Promise<{ message: string }> {
  return post<{ message: string }>('/auth/forgot-password', { email })
}

/** Réinitialise le mot de passe à partir du jeton reçu par courriel. */
export function resetPassword(corps: { token: string; password: string }): Promise<void> {
  return post<void>('/auth/reset-password', corps)
}
